import React, { useState } from 'react';
import {
  ButtonsContainer,
  TitleAndSubtitle,
  ContentButtonsWrapper,
  TextWrapper,
  ButtonWrapper,
} from '../../../layout/layout-container.styled';
import { NavButton } from '../../../nav-button/nav-button';
import { Text, font } from '../../../../style-utils/text';
import { DefaultComponentProps } from '../../../app-router/component-props';
import styled from '@emotion/styled';
import { VisitorLogState } from '../../../hooks/use-visitor-log-handler';
import { palette, radius, shadow, spacing } from '../../../../style-utils/theme';
import { down, touch } from '../../../../style-utils/media-queries';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { PersonField } from '../../../capture/person-field';
import { ScanIdButton } from '../../../capture/scan-id-button';

/* Centred under BOTH fields: one scan fills the name and the surname, so the
   button belongs to the pair rather than to either one of them. */
const ScanRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${spacing.xs};
`;

const InputAndLabelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  /* Name and surname are two separate questions and each now carries its own
     label, provenance badge and (on the given name) a scan button — without a
     gap they read as one crowded block. */
  gap: ${spacing.md};
  width: calc(100% - 30% - 32px);
  padding: ${spacing.sm};

  ${down('md')} {
    width: 100%;
  }
`;

/* Roster of guests already added via "Add New". */
const GuestList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  width: 100%;
  max-width: 480px;
  margin: 0 auto ${spacing.md};
`;

const GuestListLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${palette.slate};
  margin-bottom: ${spacing.half};
`;

/* A BUTTON, not a div: an added guest is
   the control that reopens that guest for editing, so it has to be reachable by
   keyboard and announce its pressed state. */
const GuestChip = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  width: 100%;
  padding: ${spacing.xs} ${spacing.sm};
  background: ${({ $active }) =>
    $active ? palette.irisTint : palette.irisTintSoft};
  border: 1px solid ${({ $active }) => ($active ? palette.iris : palette.line)};
  border-radius: ${radius.sm};
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  ${touch} {
    min-height: 44px;
  }
`;

const EditingBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  flex-wrap: wrap;
  margin-bottom: ${spacing.xs};
  color: ${palette.slate};
  font-size: 13px;
`;

// Exported for the desk's Expected today screen (PASS_NOT_TODAY's "Sign in as
// a visitor"), so the product keeps ONE inline text action rather than two.
export const InlineAction = styled.button`
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  font-weight: 600;
  color: ${palette.lagoon};
  text-decoration: underline;
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
    border-radius: ${radius.xs};
  }

  ${touch} {
    min-height: 44px;
  }
`;

const GuestEditHint = styled.span`
  margin-left: auto;
  flex-shrink: 0;
  color: ${palette.lagoon};
  font-size: 12px;
  font-weight: 600;
`;

const GuestIndex = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${palette.lagoon};
  color: ${palette.white};
  font-size: 12px;
  font-weight: 700;
`;

const GuestName = styled.span`
  min-width: 0;
  color: ${palette.ink};
  font-size: 14px;
  font-weight: 500;
  word-break: break-word;
`;

export const Id: React.FC<DefaultComponentProps> = ({
  navigate,
  nextPath,
  prevPath,
  state,
  changeHandler,
  addNewName,
  setCapture,
}) => {
  const { names, middleNames, lastNames, numberOfGuests, field_provenance, property_ids } =
    state as VisitorLogState;
  const buildingId = property_ids?.[0] ?? null;
  // Always-defined views. middleNames arrived after the other two, so a state
  // object from before it existed (hot reload, restored draft) has undefined
  // here — and `middleNames.length` inside an index expression is NOT covered
  // by the optional chaining on the lookup itself.
  const nameList = names || [''];
  const middleList = middleNames || [];
  const lastList = lastNames || [''];
  /**
   * WHICH GUEST THE THREE INPUTS ARE EDITING.
   *
   * It used to be, unconditionally, `nameList.length - 1` — the slot at the end
   * of the array. So the moment a guest was committed with "Add New" they became
   * a read-only chip and were unreachable: a party of four with a typo in guest
   * one could only be fixed by abandoning the entry and starting again. The
   * inputs are already index-addressed (changeHandler takes the slot), so
   * nothing about the write path had to change — only which index it was given.
   *
   * null = the new, unfilled guest at the end, which is the default and the
   * common case.
   */
  const [editingSlot, setEditingSlot] = useState<number | null>(null);
  const newGuestSlot = Math.max(nameList.length - 1, 0);
  const slot =
    editingSlot !== null && editingSlot <= newGuestSlot
      ? editingSlot
      : newGuestSlot;
  const editingExisting = slot !== newGuestSlot;

  const [key, setKey] = useState(0);
  // Which action tripped the guest-count guard (null = dialog closed).
  const [limitPrompt, setLimitPrompt] = useState<'add' | 'continue' | null>(
    null
  );

  // Guest roster: the array's last entry is the one currently being typed in
  // the inputs; everything before it is an "added" guest. Continue is allowed
  // as soon as there is at least one guest (added OR currently entered).
  const guestPairs = (names || []).map((n, i) => ({
    name: (n || '').trim(),
    lastName: (lastNames?.[i] || '').trim(),
  }));
  const addedGuests = guestPairs
    .slice(0, Math.max(guestPairs.length - 1, 0))
    .filter(g => g.name || g.lastName);
  const lastPair = guestPairs[guestPairs.length - 1];
  const guestCount =
    addedGuests.length + (lastPair?.name || lastPair?.lastName ? 1 : 0);

  // The count declared on the "No. Of Guests" step caps the roster.
  const guestLimit = Math.max(1, Number(numberOfGuests) || 1);

  const addGuestPair = () => {
    // Back to the end of the roster: "Add New" means a NEW guest, even if the
    // reader had reopened an earlier one to fix a typo.
    setEditingSlot(null);
    addNewName('names');
    addNewName('middleNames');
    addNewName('lastNames');
    setKey(key + 1);
  };

  const handleAddNew = () => {
    if (guestCount >= guestLimit) {
      setLimitPrompt('add');

      return;
    }
    addGuestPair();
  };

  const handleContinue = () => {
    if (guestCount > guestLimit) {
      setLimitPrompt('continue');

      return;
    }
    navigate(nextPath, { replace: false });
  };

  // Increasing from the dialog: match the count to what's being added.
  const increasedCount = limitPrompt === 'add' ? guestCount + 1 : guestCount;

  const confirmIncrease = () => {
    changeHandler(String(increasedCount), 'numberOfGuests');
    if (limitPrompt === 'add') {
      addGuestPair();
    } else {
      navigate(nextPath, { replace: false });
    }
    setLimitPrompt(null);
  };

  return (
    <>
      <TitleAndSubtitle direction="row">
        <TextWrapper>
          <Text as="h1" fontSize={font.size.xl} fontWeight={700} mt={0} mb={8}>
            Guest ID
          </Text>
          <Text color={font.color.gray} fontSize={font.size.xs} mt={0} mb={0}>
            {`Scan the guest's ID, or type their name`}
          </Text>
        </TextWrapper>
        <ButtonWrapper>
          <NavButton
            onClick={handleAddNew}
            disabled={false}
            text={'Add New'}
            direction={'increment'}
          />
        </ButtonWrapper>
      </TitleAndSubtitle>
      <ContentButtonsWrapper>
        {addedGuests.length > 0 && (
          <GuestList>
            <GuestListLabel>Added guests ({addedGuests.length})</GuestListLabel>
            {addedGuests.map((g, i) => (
              <GuestChip
                key={i}
                type="button"
                $active={slot === i}
                aria-pressed={slot === i}
                onClick={() => setEditingSlot(slot === i ? null : i)}
              >
                <GuestIndex>{i + 1}</GuestIndex>
                <GuestName>{`${g.name} ${g.lastName}`.trim()}</GuestName>
                <GuestEditHint>{slot === i ? 'Editing' : 'Edit'}</GuestEditHint>
              </GuestChip>
            ))}
          </GuestList>
        )}
        {/* WHICH GUEST THE FIELDS BELOW BELONG TO. Without it the inputs would
            silently change meaning when a chip is pressed — the same three
            boxes, now editing somebody else. */}
        {editingExisting && (
          <EditingBanner role="status">
            <span>Editing guest {slot + 1}.</span>
            <InlineAction type="button" onClick={() => setEditingSlot(null)}>
              Back to the new guest
            </InlineAction>
          </EditingBanner>
        )}
        <>
          <Text
            fontSize={font.size.xl}
            fontWeight={700}
            mt={0}
            mb={0}
            align="center"
          >
            Name and Last Name
          </Text>
          <InputAndLabelWrapper key={key}>
            {/* PROVENANCE, NOT RESOLUTION.
                These are the VISITORS — outsiders. This is the ID-scan path, so
                each name carries a provenance badge and stays correctable; but
                `resolves` is false, because a guest is not a resident and
                offering to link one to an occupancy would invite the concierge
                to assert something false. The HOST is the resolved person on
                this form, and that lives on the location step. */}
            <PersonField
              label="Name"
              value={nameList[slot] ?? ''}
              onChange={next =>
                changeHandler(next, 'names', undefined, slot)
              }
              resolves={false}
              source={field_provenance?.[`names.${slot}`]?.source}
              scanRaw={field_provenance?.[`names.${slot}`]?.raw}
              maxLength={30}
              placeholder="Name"
            />
            <PersonField
              label="Middle name (optional)"
              value={middleList[slot] ?? ''}
              onChange={next =>
                changeHandler(next, 'middleNames', undefined, slot)
              }
              resolves={false}
              source={
                field_provenance?.[`middleNames.${slot}`]?.source
              }
              scanRaw={field_provenance?.[`middleNames.${slot}`]?.raw}
              maxLength={50}
              placeholder="Middle name"
            />
            <PersonField
              label="Lastname"
              value={lastList[slot] ?? ''}
              onChange={next =>
                changeHandler(next, 'lastNames', undefined, slot)
              }
              resolves={false}
              source={
                field_provenance?.[`lastNames.${slot}`]?.source
              }
              scanRaw={
                field_provenance?.[`lastNames.${slot}`]?.raw
              }
              maxLength={50}
              placeholder="Lastname"
            />
            <ScanRow>
              <ScanIdButton
                buildingId={buildingId}
                // One sitting per party: the concierge scans every guest's
                // document in a row without touching the screen. Bounded by the
                // count declared on the previous step, which is also the hard
                // ceiling on paid requests for one opening of the sheet.
                sequenceTotal={guestLimit}
                label={guestLimit > 1 ? `Scan ${guestLimit} guest IDs` : 'Scan guest ID'}
                onResult={(r, i) => {
                changeHandler(r.first_name, 'names', undefined, i);
                changeHandler(r.middle || '', 'middleNames', undefined, i);
                changeHandler(r.last_name, 'lastNames', undefined, i);
                setCapture?.({
                  field_provenance: {
                    ...field_provenance,
                    [`names.${i}`]: {
                      source: 'scan',
                      raw: r.raw.first_name,
                      confidence: r.confidence,
                    },
                    [`middleNames.${i}`]: {
                      source: 'scan',
                      raw: r.raw.middle_name || '',
                      confidence: r.confidence,
                    },
                    [`lastNames.${i}`]: {
                      source: 'scan',
                      raw: r.raw.last_name,
                      confidence: r.confidence,
                    },
                  },
                });
                // Make room for the next guest while the camera stays open, so
                // slot i+1 exists when the next document is read.
                if (i + 1 < guestLimit) {
                    addNewName('names');
                    addNewName('middleNames');
                    addNewName('lastNames');
                  }
                }}
              />
            </ScanRow>
          </InputAndLabelWrapper>
        </>
      </ContentButtonsWrapper>
      <ButtonsContainer>
        <NavButton
          disabled={false}
          direction="back"
          textColor={palette.slate}
          background="transparent"
          text={'Back'}
          onClick={() => {
            navigate(prevPath, { replace: false });
          }}
        />
        <NavButton
          disabled={guestCount < 1}
          direction="forward"
          text={'Continue'}
          onClick={handleContinue}
        />
      </ButtonsContainer>
      <ConfirmDialog
        open={limitPrompt !== null}
        title="More guests than expected"
        message={`Looks like the guest count may be off — this visit is set for ${guestLimit} ${
          guestLimit === 1 ? 'guest' : 'guests'
        }, but you${
          limitPrompt === 'add'
            ? `'re about to name guest ${guestCount + 1}`
            : `'ve named ${guestCount}`
        }. Update the count, or go back and double-check it.`}
        primaryLabel={`Increase to ${increasedCount}`}
        onPrimary={confirmIncrease}
        secondaryLabel="Review guest count"
        onSecondary={() => {
          setLimitPrompt(null);
          navigate(prevPath, { replace: false });
        }}
        onDismiss={() => setLimitPrompt(null)}
      />
    </>
  );
};
